import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { formatDate } from '@/shared/lib';
import { Switch } from '@/shared/ui';
import { getOptionColors } from './getOptionColors';
import { resolveSplitOrder } from './resolveSplitOrder';
import type { ListProps } from './types';

const identity = (option: string) => option;

const List = styled('ul')(() => ({
  margin: 0,
  padding: 0,
  listStyleType: 'none',
  overflow: 'auto',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
}));
const Item = styled('li')(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  backgroundColor: 'white',
  lineHeight: 1.5,
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  '&:hover': {
    backgroundColor: 'rgba(6, 63, 254, 0.12)', // --accent-700
  },
}));

export function SplitLegendList({
  isDateContent,
  filterQuery,
  options,
  palette,
  sort,
  isChronologicalSplit,
  valueByOption,
  onOptionsToggle,
  onOptionHover,
  inactiveOptionsSet,
}: ListProps) {
  // Indexed into the UNSORTED options, as the chart assigns line colours.
  const optionColors = useMemo(() => {
    return getOptionColors(options, palette);
  }, [options, palette]);
  const preparedOptions = useMemo(() => {
    const preparedQuery = filterQuery.toLowerCase();
    const filtered = options.filter((option) => {
      return option.toLowerCase().includes(preparedQuery);
    });

    return resolveSplitOrder({
      options: filtered,
      getKey: identity,
      getLabel: identity,
      sort,
      isChronologicalSplit: !!isChronologicalSplit,
      valueByOption,
    });
  }, [options, filterQuery, sort, isChronologicalSplit, valueByOption]);
  const allOptionsChecked = useMemo(() => {
    return preparedOptions.every((option) => !inactiveOptionsSet.has(option));
  }, [preparedOptions, inactiveOptionsSet]);

  if (preparedOptions.length === 0) {
    return (
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ my: 2, textAlign: 'center' }}
      >
        Nothing found.
      </Typography>
    );
  }

  const handleOptionToggle =
    (option: string) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onOptionsToggle([option], checked);
    };
  const handleAllOptionsToggle = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onOptionsToggle(preparedOptions, checked);
  };

  return (
    <List>
      <Item sx={{ '&:hover': { backgroundColor: 'inherit' } }}>
        <Switch checked={allOptionsChecked} onChange={handleAllOptionsToggle} />
        All
      </Item>
      <Divider />
      {preparedOptions.map((option) => {
        let name = option;

        if (isDateContent) {
          name = formatDate(name);
        }

        return (
          <Item
            key={option}
            onMouseEnter={() => onOptionHover?.(option)}
            onMouseLeave={() => onOptionHover?.(null)}
          >
            <Switch
              customColor={optionColors.get(option)}
              checked={!inactiveOptionsSet.has(option)}
              onChange={handleOptionToggle(option)}
            />
            <Tooltip title={name}>
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Box>
            </Tooltip>
          </Item>
        );
      })}
    </List>
  );
}
